/**
 * SA host page — `/global-settings/unavatar` (P1, 2026-06-15).
 *
 * SA GLOBAL SETTINGS → Unavatar PRO API token. The token is the candidate-photo
 * provider key, set globally by the super-admin. It is a SECRET:
 *   - encrypted at rest server-side; NEVER returned in plaintext.
 *   - shown masked (`"••••" + last4`) once set; the input is write-only.
 *   - set/rotate (PUT) and clear (DELETE → revert to free anonymous tier).
 *
 * HoD owns finer visual/UX; this page implements the structure per the spike.
 */
import { useState, type JSX } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchPlatformSettings,
  setUnavatarToken,
  clearUnavatarToken,
  type PlatformSettings,
} from "@/lib/platformSettingsApi";
import { PageHeader } from "@/components/admin/PageHeader";
import { Button } from "@/components/common/Button";

function StatusRow({ settings }: { settings: PlatformSettings }): JSX.Element {
  if (!settings.unavatar_token) {
    return (
      <p
        data-testid="unavatar-token-status"
        data-state="unset"
        className="text-sm text-text-secondary"
      >
        No PRO token set — candidate photos use the free anonymous tier
        (rate-limited).
      </p>
    );
  }
  return (
    <p
      data-testid="unavatar-token-status"
      data-state="set"
      className="text-sm text-text-secondary"
    >
      PRO token active:{" "}
      <span className="font-mono text-text-primary">{settings.unavatar_token}</span>
      {settings.unavatar_token_set_at ? (
        <span className="text-text-tertiary">
          {" "}
          · set {new Date(settings.unavatar_token_set_at).toLocaleDateString()}
        </span>
      ) : null}
    </p>
  );
}

export default function GlobalSettingsUnavatar(): JSX.Element {
  const queryClient = useQueryClient();
  const [tokenInput, setTokenInput] = useState("");

  const settingsQ = useQuery<PlatformSettings>({
    queryKey: ["platform-settings"],
    queryFn: fetchPlatformSettings,
  });

  const setMut = useMutation({
    mutationFn: (token: string) => setUnavatarToken(token),
    onSuccess: (data) => {
      setTokenInput("");
      queryClient.setQueryData(["platform-settings"], data);
    },
  });

  const clearMut = useMutation({
    mutationFn: () => clearUnavatarToken(),
    onSuccess: (data) => {
      queryClient.setQueryData(["platform-settings"], data);
    },
  });

  const busy = setMut.isPending || clearMut.isPending;
  const tokenSet = Boolean(settingsQ.data?.unavatar_token);

  return (
    <div className="space-y-4">
      <PageHeader title="Unavatar token" kicker="Global settings" />

      <div className="max-w-xl space-y-3 rounded-lg border border-border-default bg-surface-card p-4">
        {settingsQ.isLoading ? (
          <p className="text-sm text-text-secondary" aria-busy="true">
            Loading…
          </p>
        ) : settingsQ.isError || !settingsQ.data ? (
          <p className="text-sm text-status-rejected-fg">
            Couldn&apos;t load platform settings.
          </p>
        ) : (
          <StatusRow settings={settingsQ.data} />
        )}

        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (tokenInput.trim().length >= 8) {
              setMut.mutate(tokenInput.trim());
            }
          }}
        >
          <label
            htmlFor="unavatar-token-input"
            className="block text-xs font-medium text-text-secondary"
          >
            {tokenSet ? "Rotate token" : "Set token"}
          </label>
          <input
            id="unavatar-token-input"
            type="password"
            autoComplete="off"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Paste the Unavatar PRO API token"
            className="w-full rounded-md border border-border-strong bg-surface-app px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)]"
            disabled={busy}
          />
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={setMut.isPending}
              disabled={busy || tokenInput.trim().length < 8}
            >
              {tokenSet ? "Rotate" : "Set token"}
            </Button>
            {tokenSet ? (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                loading={clearMut.isPending}
                disabled={busy}
                onClick={() => clearMut.mutate()}
              >
                Clear (free tier)
              </Button>
            ) : null}
          </div>
          {setMut.isError ? (
            <p className="text-xs text-status-rejected-fg">
              Couldn&apos;t set the token. Check it and try again.
            </p>
          ) : null}
          {clearMut.isError ? (
            <p className="text-xs text-status-rejected-fg">
              Couldn&apos;t clear the token.
            </p>
          ) : null}
        </form>
      </div>
    </div>
  );
}
